import { Injectable } from "@angular/core"
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http"
import { context, propagation, trace, SpanKind } from "@opentelemetry/api"
import { Observable } from "rxjs"
import { tap } from "rxjs/operators"
import { environment } from "src/environments/environment"
import { isPropagationAllowed } from "./observability.config"
import { OTEL_PARENT_CONTEXT } from "./observability-context"
import { ObservabilityService } from "./observability.service"

@Injectable()
export class ObservabilityHttpInterceptor implements HttpInterceptor {
  constructor(private readonly observability: ObservabilityService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const target = new URL(request.url, window.location.origin)
    const parentContext = request.context.get(OTEL_PARENT_CONTEXT) || context.active()
    const span = trace.getTracer("lex-hub-front-end").startSpan(`HTTP ${request.method}`, {
      kind: SpanKind.CLIENT,
      attributes: {
        "http.request.method": request.method,
        "server.address": target.hostname,
      },
    }, parentContext)
    const spanContext = trace.setSpan(parentContext, span)
    const startedAt = performance.now()
    let completed = false
    let tracedRequest = request

    if (isPropagationAllowed(request.url, environment.observability.propagationOrigins)) {
      const headers: Record<string, string> = {}
      propagation.inject(spanContext, headers)
      tracedRequest = request.clone({ setHeaders: headers })
    }

    return next.handle(tracedRequest).pipe(tap({
      next: (event) => {
        if (event instanceof HttpResponse) {
          completed = true
          this.observability.trackHttp(span, request.method, target.hostname, event.status, undefined, startedAt)
        }
      },
      error: (error) => {
        completed = true
        this.observability.trackHttp(span, request.method, target.hostname, error?.status, error, startedAt)
      },
      complete: () => {
        if (!completed) this.observability.trackHttp(span, request.method, target.hostname, undefined, undefined, startedAt)
      },
    }))
  }
}
